import { useState, useRef } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

//components
import DataView from 'Components/DataView';
import ChipStatus from 'Components/ChipStatus';
import { Button } from "@gpstrackit/react-controls";
import { NotificationManager } from 'Components/Notifications';

//Views
import CreateEditUnitType from 'Modules/manufacturers/.components/unitTypesTab/.components/createEditUnitType';
import DeleteUnitTypes from 'Modules/manufacturers/.components/unitTypesTab/.components/deleteUnitType';
import ShowUnitTypeEvents from 'Modules/manufacturers/.components/unitTypesTab/.components/showUnitTypeEvents';

//Helpers
import { idsValidation } from 'Core/data/Helpers';

//Permission
import { HasPermission } from "Core/security";

import { unitTypeCreateEditOpenModal, unitTypeDeleteOpenModal, showUnitTypeEventsOpenModal } from 'Redux/actions';

//Styles
import './index.scss';

const UnitTypeTab = (props) => {
    const { height, manufacturer } = props;
    const { messages } = useIntl();
    const tableRef = useRef();
    const dispatch = useDispatch();

    const user = useSelector(state => state.securityRedux.user);
    const openModalCreateUnitType = useSelector(state => state.createEditUnitTypeRedux.openModal);
    const openModalDeleteUnitType = useSelector(state => state.deleteUnitTypeRedux.openModal);
    const openModalShowUnitTypeEvents = useSelector(state => state.showUnitTypeEventsRedux.openModal);

    const [columns, setColumns] = useState([
        {
            key: "id",
            title: messages['unit_type_id'],
            show: true,
            resize: true,
            width: 50,
            sort: true,
            export: true,
            functionString: 'return row?.id',
            render: (item) => {
                return <span>{item?.id}</span>
            }
        },
        {
            key: "name",
            title: messages['unit_type_name'],
            field: "name",
            show: true,
            resize: true,
            sort: true,
            export: true,
            functionString: 'return row?.name',
            render: (item) => {
                return <span>{item?.name}</span>
            }
        },
        {
            key: "typeSim",
            title: messages['unit_type_uses_sim'],
            field: "typeSim",
            show: true,
            resize: true,
            sort: true,
            export: true,
            functionString: 'return row?.typeSim ? "Yes" : "No"',
            render: (item) => {
                return <span>{item?.typeSim ? messages['common_yes'] : messages['common_no']}</span>
            }
        },
        {
            key: "typeUseImei",
            title: messages['unit_type_use_imei_as_serial'],
            field: "typeUseImei",
            show: true,
            resize: true,
            sort: true,
            export: true,
            functionString: 'return row?.typeUseImei ? "Yes" : "No"',
            render: (item) => {
                return <span>{item?.typeUseImei ? messages['common_yes'] : messages['common_no']}</span>
            }
        },
        {
            key: "active",
            title: messages['unit_type_status'],
            field: "active",
            show: true,
            resize: true,
            width: 250,
            export: true,
            functionString: 'return row?.active ? "Active" : "Inactive"',
            render: (item) => {
                return <ChipStatus statusCode={item?.active ? 1 : 20} messages={messages} />
            }
        },
    ])
    const [searchFields, setSearchFields] = useState(["name"]);

    return (
        <div style={{ marginTop: 10 }}>
            <DataView
                ref={tableRef}
                columns={columns}
                name={`unittypes-tab-${manufacturer?.name}`}
                exportName="unittypes"
                dinamicQuery
                entity="unit-types-tab"
                graphqlFunction="searchUnitTypes"
                graphqlSort="UnitTypeSortInput"
                graphqlConditions="UnitTypeSearchInput"
                customQuery="id"
                onChangeConditions={(tags, conditions, total, newColumns, rows, sort) => {
                    let currentConditions = {}
                    let currentTags = {}
                    if (!conditions && tags) {
                        currentConditions = tableRef?.current?.conditions;
                        currentTags = tags;
                    } else {
                        currentConditions = conditions || [];
                        currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
                        if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);
                    }
                    currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'id' && item?.fields[0] !== 'name' && item?.fields[0] !== 'manufacturer_name');

                    let tagsById = currentTags.filter((item) => typeof item.id === "number");
                    if (tagsById?.length > 0) {
                        //New Items
                        let currentIds = tagsById.map((item) => (item.id));
                        if (currentIds.length > 0) {
                            currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
                        }
                    }
                    let tagsByLabel = currentTags.filter(item => typeof item.id === "string")
                    if (tagsByLabel.length > 0) {
                        let currentLabels = tagsByLabel.map((item) => (item.name))
                        if (currentLabels.length > 0) {
                            currentConditions.push({ fields: searchFields, terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
                        }
                    }

                    currentConditions && currentConditions.push({ fields: ["manufacturer_name"], terms: [manufacturer?.name], exact: true, conditionOperator: "AND" });
                    if (!conditions && tags) tableRef?.current?.getData(null, null, null, currentConditions, newColumns);
                    else tableRef?.current?.getData(null, rows, sort, currentConditions);
                }}
                searchFields={searchFields}
                //search
                asyncSelect
                searchConditions={["name"]}
                searchOptionValue="id"
                searchOptionLabel="name"
                customSearch={{ fields: ["manufacturer_name"], terms: [manufacturer?.name], exact: true }}
                noSplit
                onChangeColumns={(items) => setColumns(items)}
                tableButtons={["export", 'orderColumn']}
                noFilters
                height={height}
                menuOptions={() => {
                    return [
                        messages['edit'],
                        messages['delete'],
                        messages['unit_type_events'],
                    ]
                }}
                options={[
                    {
                        id: "edit-unit-type",
                        label: messages['edit'],
                        show: true,
                        disabled: !HasPermission(user, "untty", "u"),
                        single: true,
                        action: (items) => {
                            dispatch(unitTypeCreateEditOpenModal(items[0]));
                        }
                    },
                    {
                        id: "delete-unit-type",
                        label: messages['delete'],
                        show: true,
                        disabled: !HasPermission(user, "untty", "d"),
                        single: true,
                        action: (items) => {
                            dispatch(unitTypeDeleteOpenModal(items[0]));
                        }
                    },
                    {
                        id: "events-unit-type",
                        label: messages['unit_type_events'],
                        show: true,
                        disabled: !HasPermission(user, "untty"),
                        single: true,
                        action: (items) => {
                            dispatch(showUnitTypeEventsOpenModal(items[0]));
                        }
                    },
                ]}
                onClickMenu={(event, item) => {
                    if (event === messages['edit']) {
                        if (HasPermission(user, "untty", "u")) {
                            dispatch(unitTypeCreateEditOpenModal(item))
                        } else {
                            NotificationManager.warning(
                                messages['common_error_permission'],
                                messages['edit'],
                                3000,
                            );
                        }
                    }
                    if (event === messages['unit_type_events']) {
                        if (HasPermission(user, "untty", "u")) {
                            dispatch(showUnitTypeEventsOpenModal(item))
                        } else {
                            NotificationManager.warning(
                                messages['common_error_permission'],
                                messages['edit'],
                                3000,
                            );
                        }
                    }
                    if (event === messages['delete']) {
                        if (HasPermission(user, "untty", "d")) {
                            dispatch(unitTypeDeleteOpenModal(item))
                        } else {
                            NotificationManager.warning(
                                messages['common_error_permission'],
                                messages['delete'],
                                3000,
                            );
                        }
                    }
                }}
                extraButton={() => {
                    return (
                        <div style={{ paddingLeft: 2 }}>
                            <Button
                                onClick={() => dispatch(unitTypeCreateEditOpenModal())}
                                className="btn-dark-blue"
                                disabled={!HasPermission(user, "untty", "c")}
                                style={{ height: 42 }}
                            >
                                {messages['unit_type_add_new']}
                            </Button>
                        </div>
                    )
                }}
            />
            {openModalCreateUnitType && <CreateEditUnitType manufacturer={manufacturer} tableRef={tableRef} />}
            {openModalDeleteUnitType && <DeleteUnitTypes tableRef={tableRef} />}
            {openModalShowUnitTypeEvents && <ShowUnitTypeEvents manufacturerId={manufacturer?.id} manufacturer={manufacturer} />}
        </div>
    )
}

export default UnitTypeTab;